// @ts-nocheck
import type { Ticket, Comment, Customer, Assignee } from '../schema/types';
import { customerKey } from '../lib/customerLink';

const COLORS = [
  'linear-gradient(135deg,#0ea5e9,#6366f1)',
  'linear-gradient(135deg,#f97316,#db2777)',
  'linear-gradient(135deg,#10b981,#0ea5e9)',
  'linear-gradient(135deg,#a855f7,#ec4899)',
  'linear-gradient(135deg,#06b6d4,#3b82f6)',
  'linear-gradient(135deg,#84cc16,#22c55e)',
  'linear-gradient(135deg,#f59e0b,#dc2626)',
];

function colorFor(str: string): string {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  return COLORS[Math.abs(h) % COLORS.length];
}

function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(p => p[0] || '').join('').toUpperCase() || '?';
}

function dateLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Exact date + time ("Jun 8, 2026, 3:45 PM") for arrival / resolution stamps.
function dateTimeLabel(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
}

/**
 * DEPRECATED — do not use for anything new, and prefer `ticket.sla` wherever
 * this still appears.
 *
 * This derives a countdown from first_response_due_at and the wall clock,
 * which is exactly the second calculation SKTICK-CR-001 WP-5 exists to
 * eliminate. It knows nothing about pause: SLA timers stop in `pending` and
 * `on_hold` and accumulate paused_total_seconds, so on a paused ticket this
 * number keeps ticking down while the real clock is stopped — and disagrees
 * with the ticket, with the escalation sweeper and with SK HQ Console.
 *
 * The server now sends resolved state on list and detail (`sla.overdue`,
 * `sla.effectiveRemainingSeconds`, `sla.isPaused`), already net of pause.
 * Render that. This is kept only so existing callers keep compiling until they
 * are moved across.
 */
function slaMinutes(ticket: any): number | null {
  if (!ticket.firstResponseDueAt) return null;
  return Math.floor((new Date(ticket.firstResponseDueAt).getTime() - Date.now()) / 60000);
}

function mapStatus(s: string): 'open' | 'pending' | 'resolved' | 'closed' {
  if (s === 'pending' || s === 'on_hold') return 'pending';
  if (s === 'resolved') return 'resolved';
  if (s === 'closed') return 'closed';
  return 'open'; // open, in_progress, reopened all map to open
}

function mapChannel(src: string): 'email' | 'web' | 'api' | 'agent' {
  if (src === 'email') return 'email';
  if (src === 'web' || src === 'chat' || src === 'mobile') return 'web';
  if (src === 'api') return 'api';
  return 'agent';
}

export function mapApiTicket(apiTicket: any, comments: any[] = []): Ticket {
  const email = apiTicket.requesterEmail || 'unknown@unknown.com';
  const namePart = email.split('@')[0].replace(/[._-]/g, ' ');
  const name = namePart.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const customer: Customer = {
    name,
    email,
    initials: initialsOf(name),
    color: colorFor(email),
    org: '—',
    tickets: 0,
    since: new Date(apiTicket.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
  };

  const assignee: Assignee | null = apiTicket.assignedTo ? {
    name: apiTicket.assignedTo,
    initials: apiTicket.assignedTo.slice(0, 2).toUpperCase(),
    color: colorFor(apiTicket.assignedTo),
  } : null;

  const lastActivityIso: string | null =
    apiTicket.lastActivityAt || apiTicket.updatedAt || apiTicket.createdAt || null;

  const mapped: any = {
    id: `TKT-${apiTicket.number}`,
    _apiId: apiTicket.id, // real ULID for API calls
    assignedTo: apiTicket.assignedTo || null, // raw platform user id of the assignee (for "assigned to me" checks)
    // INTERNAL space partition (SKTICK-INTSPACE-001): the space this ticket
    // belongs to + its resolved requester. `requester.kind` (employee vs
    // contact) is derived from the owning space's type in the store, so the
    // employee chip / lockups render without a second round-trip.
    spaceId: apiTicket.spaceId || null,
    // SKTICK-CR-001 WP-2 — opaque SK Platform node (outlet / region / zone).
    // A filter dimension and a label; this product never resolves the tree.
    nodeId: apiTicket.nodeId || null,
    nodeLabel: apiTicket.nodeLabel || null,
    // WP-5 — resolved SLA state, computed server-side net of pause. Render it;
    // never recompute it. Undefined means "this path did not load timers",
    // which is not the same as "this ticket has no SLA".
    sla: apiTicket.sla,
    // WP-8 — HQ's reopen-cap counter, and the strongest signal on a ticket
    // that something is not actually fixed.
    reopenCount: typeof apiTicket.reopenCount === 'number' ? apiTicket.reopenCount : 0,
    // Unvalidated by design — HQ owns the shape. Carried raw so the evidence
    // panel can read customFields.hq* defensively.
    customFields: apiTicket.customFields || {},
    // Raw backend source, distinct from the mapped `channel` below.
    _apiSource: apiTicket.source || null,
    requesterId: apiTicket.requesterId || null,
    requesterEmail: apiTicket.requesterEmail || email,
    subject: apiTicket.subject,
    status: mapStatus(apiTicket.status),
    _apiStatus: apiTicket.status, // raw backend status (open|in_progress|pending|on_hold|resolved|closed) for detail display; `status` above is the 4-bucket value lists/filters depend on
    priority: (apiTicket.priority as any) || 'medium',
    category: apiTicket.category || apiTicket.type || 'General',
    type: apiTicket.type || null,
    description: apiTicket.description || null,
    channel: mapChannel(apiTicket.source || 'email'),
    vertical: apiTicket.vertical || null,
    customer,
    assignee,
    watchers: [],
    tags: apiTicket.tags || [],
    replyTo: apiTicket.replyTo || apiTicket.requesterEmail || 'support@softknack.com',
    slaMinutes: slaMinutes(apiTicket),
    openedAgo: timeAgo(apiTicket.createdAt),
    createdAtISO: apiTicket.createdAt || null,
    createdAtLabel: apiTicket.createdAt ? dateLabel(apiTicket.createdAt) : '—',
    // Last activity is the backend `lastActivityAt` (falls back to updatedAt,
    // then createdAt). Drives the "X ago" stamp so it reflects real movement.
    lastActivityISO: lastActivityIso,
    lastActivityAgo: lastActivityIso ? timeAgo(lastActivityIso) : timeAgo(apiTicket.createdAt),
    lastActivityLabel: lastActivityIso ? dateLabel(lastActivityIso) : '—',
    // Exact resolution time from the backend `resolvedAt` (null when unresolved).
    resolvedAtISO: apiTicket.resolvedAt || null,
    resolvedAtLabel: apiTicket.resolvedAt ? dateTimeLabel(apiTicket.resolvedAt) : null,
    comments: comments.map(mapApiComment),
  };

  return mapped as Ticket;
}

export function mapApiComment(c: any): Comment {
  return {
    id: c.id,
    type: c.isInternal ? 'internal' : (c.authorType === 'system' ? 'system-bounce' : c.authorType === 'customer' ? 'customer' : 'public') as any,
    author: c.authorDisplayName || c.authorPlatformUserId || 'System',
    // The author's platformUserId — drives the "can I edit/delete this?" gate
    // on the FE (the backend also enforces author-only).
    authorPlatformUserId: c.authorPlatformUserId || null,
    time: new Date(c.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    body: c.body,
    viaEmail: c.viaEmail || false,
    editedAt: c.editedAt || null,
  };
}

// Extract unique customers from tickets list — one per contact id when the
// ticket is linked to a contact, else one per email (lib/customerLink).
export function extractCustomers(tickets: Ticket[]): Customer[] {
  const seen = new Set<string>();
  const out: Customer[] = [];
  for (const t of tickets) {
    const key = customerKey(t.customer);
    if (!seen.has(key)) {
      seen.add(key);
      out.push({ ...t.customer, tickets: tickets.filter(x => customerKey(x.customer) === key).length });
    }
  }
  return out;
}
